/**
 * L2 drill-down: every Azure Monitor alert the backend collected, filterable
 * by severity, state, and subscription — all held in the URL so a filtered
 * view is shareable and the back button walks back up the drill path
 * (dashboard design rules §1 / §4.2).
 *
 * Honesty: the list is capped at the 50 most severe while the header counts
 * cover the whole window; the cap is stated whenever it bites.
 */

import {
  Alert,
  Badge,
  type BreadcrumbItem,
  Breadcrumbs,
  DataGrid,
  EmptyState,
  Freshness,
  PageHeader,
  Skeleton,
  Typography,
} from "@norican/react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { DetailDrawer, FilterRow } from "@/components/board";
import { type CloudAlert, useCloudAlerts, useCloudOverview } from "@/lib/api";
import { formatCount, SEVERITY_ORDER, severityStatus } from "@/lib/charts/config";
import { useDrawerParam, useDrillParams } from "@/lib/drill";

import { AlertFrequencyPanel } from "./alert-frequency-panel";
import { PanelCard } from "./panel-card";
import { ALERT_DRAWER_PARAM, ALERT_STATE_PARAM, SEVERITY_PARAM, SUB_PARAM } from "./params";
import { formatTimestamp, resourceNameFromId, shortType } from "./status";

const BREADCRUMBS: BreadcrumbItem[] = [
  {
    label: "Cloud",
    render: ({ className, children }: { className: string; children: ReactNode }) => (
      <Link to="/cloud" className={className}>
        {children}
      </Link>
    ),
  },
  { label: "Alerts", current: true },
];

export function CloudAlertsPage() {
  const alerts = useCloudAlerts();
  const overview = useCloudOverview();
  const drill = useDrillParams(SEVERITY_PARAM, ALERT_STATE_PARAM, SUB_PARAM);
  const drawer = useDrawerParam(ALERT_DRAWER_PARAM);

  const data = alerts.data;
  const severities = drill.getList(SEVERITY_PARAM);
  const states = drill.getList(ALERT_STATE_PARAM);
  const subIds = drill.getList(SUB_PARAM);

  const rows = (data?.alerts ?? []).filter(
    (alert) =>
      (severities.length === 0 || severities.includes(alert.severity)) &&
      (states.length === 0 || states.includes(alert.monitor_condition)) &&
      (subIds.length === 0 || subIds.includes(alert.subscription_id)),
  );

  const openAlert = drawer.value ? data?.alerts.find((alert) => alert.id === drawer.value) : null;

  const subOptions = (overview.data?.subscriptions ?? []).map((subscription) => ({
    value: subscription.subscription_id,
    label: subscription.name,
  }));

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <Breadcrumbs items={BREADCRUMBS} />
      <PageHeader
        title="Azure Monitor alerts"
        subtitle="Every alert in the collection window: active plus resolved in the last 7 days."
        actions={
          data?.collected_at ? <Freshness label="Collected" since={data.collected_at} /> : undefined
        }
      />

      {alerts.error && (
        <Alert tone="danger" title="Couldn't load the alert list">
          {alerts.error instanceof Error ? alerts.error.message : "The NOVA API did not respond."}
        </Alert>
      )}

      {alerts.isPending && !data ? (
        <Skeleton height="24rem" />
      ) : (
        data &&
        (!data.configured ? (
          <PanelCard title="Alerts" caption="Not available">
            <Typography variant="body-sm" tone="muted">
              {data.status_detail}
            </Typography>
          </PanelCard>
        ) : (
          <>
            <FilterRow
              showRange={false}
              dimensions={[
                {
                  param: SEVERITY_PARAM,
                  label: "Severity",
                  noun: "severity",
                  options: SEVERITY_ORDER.map((severity) => ({
                    value: severity,
                    label: severityStatus(severity).label,
                  })),
                },
                {
                  param: ALERT_STATE_PARAM,
                  label: "State",
                  noun: "state",
                  options: [
                    { value: "Fired", label: "Firing" },
                    { value: "Resolved", label: "Resolved" },
                  ],
                },
                {
                  param: SUB_PARAM,
                  label: "Subscription",
                  noun: "subscription",
                  options: subOptions,
                },
              ]}
            />

            <PanelCard
              title="Alerts"
              caption={`${formatCount(rows.length)} of ${formatCount(
                data.alerts.length,
              )} listed · ${formatCount(data.total)} in the window`}
              padding={rows.length === 0 ? "md" : "none"}
            >
              {rows.length === 0 ? (
                <EmptyState
                  size="sm"
                  title="No alerts match these filters"
                  description="Clear a filter to widen the list."
                />
              ) : (
                <div className="h-[32rem]">
                  {/* DataGrid needs a definite height for its sticky header/scroller. */}
                  <DataGrid<CloudAlert>
                    data={rows}
                    getRowId={(alert) => alert.id}
                    getRowSearchText={(alert) =>
                      `${alert.name} ${alert.subscription_name} ${alert.target_resource ?? ""}`
                    }
                    density="compact"
                    aria-label="All collected Azure Monitor alerts"
                    columns={[
                      {
                        id: "severity",
                        header: "Severity",
                        accessor: (alert) => alert.severity,
                        sortable: true,
                        cell: (alert) => (
                          <Badge
                            tone={severityStatus(alert.severity).tone}
                            variant="subtle"
                            size="xs"
                          >
                            {severityStatus(alert.severity).short}
                          </Badge>
                        ),
                      },
                      {
                        id: "name",
                        header: "Alert",
                        accessor: (alert) => alert.name,
                        sortable: true,
                        cell: (alert) => (
                          <button
                            type="button"
                            className="text-fg text-left font-medium hover:underline"
                            onClick={() => drawer.open(alert.id)}
                          >
                            {alert.name}
                          </button>
                        ),
                      },
                      {
                        id: "state",
                        header: "State",
                        accessor: (alert) => alert.monitor_condition,
                        sortable: true,
                        cell: (alert) => (
                          <Badge
                            tone={alert.monitor_condition === "Fired" ? "danger" : "neutral"}
                            variant={alert.monitor_condition === "Fired" ? "subtle" : "outline"}
                            size="xs"
                          >
                            {alert.monitor_condition}
                          </Badge>
                        ),
                      },
                      {
                        id: "resource",
                        header: "Resource",
                        accessor: (alert) => resourceNameFromId(alert.target_resource),
                        sortable: true,
                        cell: (alert) => (
                          <div className="flex flex-col">
                            <span className="text-fg text-sm">
                              {resourceNameFromId(alert.target_resource)}
                            </span>
                            {alert.target_resource_type && (
                              <span className="text-fg-muted text-xs">
                                {shortType(alert.target_resource_type)}
                              </span>
                            )}
                          </div>
                        ),
                      },
                      {
                        id: "subscription",
                        header: "Subscription",
                        accessor: (alert) => alert.subscription_name,
                        sortable: true,
                        cell: (alert) => (
                          <span className="text-fg-muted text-sm">{alert.subscription_name}</span>
                        ),
                      },
                      {
                        id: "fired",
                        header: "Fired",
                        accessor: (alert) => alert.fired_at ?? "",
                        sortable: true,
                        cell: (alert) => (
                          <span className="text-fg-muted text-xs tabular-nums">
                            {formatTimestamp(alert.fired_at)}
                          </span>
                        ),
                      },
                      {
                        id: "resolved",
                        header: "Resolved",
                        accessor: (alert) => alert.resolved_at ?? "",
                        sortable: true,
                        cell: (alert) => (
                          <span className="text-fg-muted text-xs tabular-nums">
                            {formatTimestamp(alert.resolved_at)}
                          </span>
                        ),
                      },
                    ]}
                  />
                </div>
              )}
            </PanelCard>

            {data.truncated && (
              <Typography variant="body-sm" tone="muted">
                Showing the {data.alerts.length} most severe alerts — the window holds{" "}
                {formatCount(data.total)}.
              </Typography>
            )}

            {/* The list above is a snapshot; this is the history behind it —
                read back from `alert_event`, unaffected by the filters, which
                only narrow the live window. */}
            <AlertFrequencyPanel alerts={data} />

            {data.errors.length > 0 && (
              <Typography variant="body-sm" tone="muted">
                Collection problems: {data.errors.join(" · ")}
              </Typography>
            )}

            {openAlert && (
              <DetailDrawer
                open
                onClose={drawer.close}
                title={openAlert.name}
                breadcrumbs={[
                  { label: "Cloud", to: "/cloud" },
                  { label: "Alerts", to: "/cloud/alerts" },
                ]}
                items={[
                  { label: "Severity", value: severityStatus(openAlert.severity).label },
                  { label: "State", value: `${openAlert.state} · ${openAlert.monitor_condition}` },
                  { label: "Fired", value: formatTimestamp(openAlert.fired_at) },
                  { label: "Resolved", value: formatTimestamp(openAlert.resolved_at) },
                  { label: "Resource", value: resourceNameFromId(openAlert.target_resource) },
                  {
                    label: "Resource type",
                    value: openAlert.target_resource_type
                      ? shortType(openAlert.target_resource_type)
                      : "—",
                  },
                  { label: "Subscription", value: openAlert.subscription_name },
                  { label: "Signal", value: openAlert.signal_type ?? "—" },
                  {
                    label: "Rule",
                    value: openAlert.alert_rule ? resourceNameFromId(openAlert.alert_rule) : "—",
                  },
                  { label: "Description", value: openAlert.description ?? "—" },
                ]}
              />
            )}
          </>
        ))
      )}
    </main>
  );
}
